// packages/api/src/hooks/useLegalTerms.ts
//
// Phase 01 onboarding-completion (W2.11) — legal terms versioning workflow.
// Consumed by /admin/legal-terms admin pages + the ReacceptanceModal
// rendered by the global layout.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import type {
  CreateLegalTextPayload,
  LegalAcceptanceText,
  LegalTermsListResponse,
  PromoteLegalTextPayload,
  ReacceptPayload,
  SnoozeReacceptancePayload,
  UpdateLegalTextPayload,
} from '@alamo/types';

import { apiClient } from '../client';
import { legalTermsKeys, queryKeys } from '../query-keys';

interface ApiSuccessEnvelope<T> {
  data: T;
}

// ─── ADMIN ──────────────────────────────────────────────────────────

export function useLegalTerms() {
  return useQuery({
    queryKey: legalTermsKeys.list(),
    queryFn: async () => {
      const res =
        await apiClient.get<ApiSuccessEnvelope<LegalTermsListResponse>>('/admin/legal-terms');
      return res.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateLegalText() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: CreateLegalTextPayload) => {
      const res = await apiClient.post<ApiSuccessEnvelope<LegalAcceptanceText>>(
        '/admin/legal-terms',
        payload,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: legalTermsKeys.all });
      toast.success(t('common:messages.created_success'));
    },
    onError: () => {
      toast.error(t('errors:unknown_error'));
    },
  });
}

export function useUpdateLegalText() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (input: { id: string; payload: UpdateLegalTextPayload }) => {
      const res = await apiClient.patch<ApiSuccessEnvelope<LegalAcceptanceText>>(
        `/admin/legal-terms/${input.id}`,
        input.payload,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: legalTermsKeys.all });
      toast.success(t('common:messages.updated_success'));
    },
    onError: () => {
      toast.error(t('errors:unknown_error'));
    },
  });
}

export function useSubmitLegalTextForApproval() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<ApiSuccessEnvelope<LegalAcceptanceText>>(
        `/admin/legal-terms/${id}/submit`,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: legalTermsKeys.all });
      toast.success(t('common:messages.updated_success'));
    },
    onError: () => {
      toast.error(t('errors:unknown_error'));
    },
  });
}

/**
 * POST /admin/legal-terms/:id/promote — requires admin 2FA OTP.
 * Side-effects (backend): supersedes previous version + bulk-populates
 * pending_acceptance rows for users who accepted the previous version.
 */
export function usePromoteLegalText() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (input: { id: string; payload: PromoteLegalTextPayload }) => {
      const res = await apiClient.post<ApiSuccessEnvelope<LegalAcceptanceText>>(
        `/admin/legal-terms/${input.id}/promote`,
        input.payload,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: legalTermsKeys.all });
      // /auth/me pending_reacceptance may flip; invalidate so other users
      // browsing in another tab refetch on next nav.
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
      toast.success(t('common:messages.published_success'));
    },
    onError: () => {
      toast.error(t('errors:unknown_error'));
    },
  });
}

// ─── USER FACING ────────────────────────────────────────────────────

/**
 * POST /legal-acceptances/reaccept — user accepts the pending version.
 * Side-effect: backend deletes the pending row, /auth/me will return
 * pending_reacceptance=null on next call.
 */
export function useReacceptLegalText() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: ReacceptPayload) => {
      const res = await apiClient.post<ApiSuccessEnvelope<{ textId: string; accepted: boolean }>>(
        '/legal-acceptances/reaccept',
        payload,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
      toast.success(t('common:messages.accepted_success'));
    },
    onError: () => {
      toast.error(t('errors:unknown_error'));
    },
  });
}

/** POST /legal-acceptances/snooze — defer the prompt up to 7 days. */
export function useSnoozeReacceptance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SnoozeReacceptancePayload) => {
      const res = await apiClient.post<
        ApiSuccessEnvelope<{ textId: string; dismissedUntil: string }>
      >('/legal-acceptances/snooze', payload);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
    },
  });
}
